import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  Tab,
  Tabs,
  TabList,
  TabPanel,
} from '@lifeomic/chroma-react/components/Tabs';
import { useHistory, useParams } from 'react-router-dom';
import SubjectViewerTableModule from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Table';
import { ConfiguredMomentFormat } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/util/momentFormats';
import {
  Category,
  DataSourceType,
  TableModuleColumnConfig,
  Tag,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/util/types';
import { TimeFilterConfig } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/hooks/usePatientViewerFhirSearch';
import {
  DashboardConfig,
  ModuleConfig,
} from '@lifeomic/phc-web-toolkit/src/components/LayoutConfiguration/types';
import urls from '../../../../util/urls';
import MemberDetail from './index';
import { memberDetailMessages } from './messages';
import { NotesContent } from './Notes';
import { MedicationsContent } from './Medications';
import { ProceduresContent } from './Procedures';
import { ConditionsContent } from './Conditions';
import { AllergiesContent } from './Allergies';

type DocumentsModuleComponent = {
  displayAsDuration: boolean;
  ocrViewerBasePath: string;
  dataSource: DataSourceType;
  columns: TableModuleColumnConfig[];
  momentFormat: ConfiguredMomentFormat;
  pageSize: number;
  useTagFilter: boolean;
  useNotTagFilter: boolean;
  notTagFilters: Tag[];
  categories: Category[];
  useCategoryFilter: boolean;
  timeFilterProps: TimeFilterConfig;
  applyDateFilter: boolean;
  filters: Tag[];
};

const documentsModuleConfig: ModuleConfig<DocumentsModuleComponent> = {
  version: 2,
  module: {
    key: 'b6bd6742-ce71-4e71-b79e-6ecb8a5bfc04',
    moduleType: '83387f5f-0e8c-456d-b528-7c1f62bcc5e4',
    title: 'Member Documents',
    layout: {
      w: 12,
      h: 20,
      x: 0,
      y: 0,
      minW: 2,
      minH: 0,
      moved: false,
      isResizable: true,
    },
    hasDynamicHeight: true,
    hasDynamicWidth: false,
    preCollapsedHeight: undefined,
    isHeaderlessModule: false,
  },
  component: {
    ocrViewerBasePath: '/project/ocr/viewer',
    dataSource: 'DOCUMENT_REFERENCE_DATA_SOURCE_TYPE',
    columns: [
      {
        label: 'Title',
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath: 'type.text',
      },
      {
        key: 'fde4b1e4-a6b2-47dd-b414-c11b9161e4c7',
      },
      {
        label: 'Source',
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath: "meta.tag[?system=='http://lifeomic.com/fhir/source-type']",
        jmesResultParser: 'coding',
      },
      {
        label: 'Type',
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath: "meta.tag[?system=='http://lifeomic.com/fhir/document-type']",
        jmesResultParser: 'coding',
      },
      {
        key: '6fb08933-f452-4923-83de-603b3f66478a',
        label: 'Date',
      },
      {
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath:
          "meta.tag[?system=='http://lifeomic.com/fhir/resource-visibility' && code=='clinic-only'] | [0].code && 'No' || 'Yes'",
        label: 'Viewable by Member?',
      },
      {
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath:
          "meta.tag[?system=='http://lifeomic.com/fhir/resource-visibility' && code=='clinic-only'] && extension[?url=='http://lifeomic.com/fhir/visibility-reason' && valueString=='file-ownership-validation-status'] | [0].valueString && 'Yes' || 'No'",
        label: 'Hidden by Validation?',
      },
      {
        key: '88f766a2-e196-4474-98de-ed7b36c8c161',
        jmesPath:
          "meta.tag[?system=='http://lifeomic.com/fhir/file-ownership-validation-status'] | [0].code || 'not scanned'",
        label: 'AI Validation status',
      },
    ],
    displayAsDuration: false,
    momentFormat: 'M/D/YYYY',
    pageSize: 200,
    useTagFilter: false,
    useNotTagFilter: true,
    notTagFilters: [
      {
        system: 'http://lifeomic.com/fhir/source',
        code: 'LifeOmic GA4GH Service',
      },
    ],
    categories: [],
    useCategoryFilter: false,
    timeFilterProps: {
      DocumentReference: 'created',
    },
    applyDateFilter: false,
    filters: [],
  },
};

const documentsDashboardConfig: DashboardConfig = {
  version: 1,
  modules: [documentsModuleConfig],
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  tabsRoot: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  tabListWrapper: {
    width: '100%',
    backgroundColor: theme.palette.common.white,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
  },
  tabList: {
    flexShrink: 0,
    alignSelf: 'flex-start',
    display: 'inline-flex',
  },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.common.white,
    position: 'relative',
  },
}));

type DocumentsContentProps = {
  member: fhir.Patient | null;
  fallback: React.ReactNode;
};

export const DocumentsContent: React.FC<DocumentsContentProps> = ({
  member,
  fallback,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { documentsTab, memberId: memberIdFromRoute } = useParams<{
    documentsTab?: string;
    memberId?: string;
  }>();

  const isValidTab = (value: string | undefined) =>
    value === 'documents' ||
    value === 'notes' ||
    value === 'medications' ||
    value === 'procedures' ||
    value === 'conditions' ||
    value === 'allergies';

  const selectedTab:
    | 'documents'
    | 'notes'
    | 'medications'
    | 'procedures'
    | 'conditions'
    | 'allergies' = isValidTab(documentsTab)
    ? (documentsTab as
        | 'documents'
        | 'notes'
        | 'medications'
        | 'procedures'
        | 'conditions'
        | 'allergies')
    : 'documents';

  const memberId = member?.id ?? memberIdFromRoute ?? '';
  const getDocumentsPath = useCallback(
    (
      tab:
        | 'documents'
        | 'notes'
        | 'medications'
        | 'procedures'
        | 'conditions'
        | 'allergies',
    ) =>
      tab === 'documents'
        ? `${urls.app.members(memberId)}/documents`
        : `${urls.app.members(memberId)}/documents/${tab}`,
    [memberId],
  );

  React.useEffect(() => {
    if (memberId && documentsTab && !isValidTab(documentsTab)) {
      history.replace(getDocumentsPath('documents'));
    }
  }, [documentsTab, getDocumentsPath, history, memberId]);

  if (!member) {
    return <>{fallback}</>;
  }

  const patient = { ...member, id: member.id ?? '' } as fhir.Patient & {
    id: string;
  };

  return (
    <div className={classes.root}>
      <div className={classes.tabsContainer}>
        <Tabs selectedTabId={selectedTab} variant="pill">
          <div className={classes.tabsRoot}>
            <div className={classes.tabListWrapper}>
              <TabList
                aria-label="Documents, notes, medications, procedures, conditions, and allergies sections"
                className={classes.tabList}
              >
                <Tab
                  stopId="documents"
                  onClick={() => history.push(getDocumentsPath('documents'))}
                >
                  {formatMessage(memberDetailMessages.documents)}
                </Tab>
                <Tab
                  stopId="notes"
                  onClick={() => history.push(getDocumentsPath('notes'))}
                >
                  {formatMessage(memberDetailMessages.notes)}
                </Tab>
                <Tab
                  stopId="medications"
                  onClick={() => history.push(getDocumentsPath('medications'))}
                >
                  {formatMessage(memberDetailMessages.medications)}
                </Tab>
                <Tab
                  stopId="procedures"
                  onClick={() => history.push(getDocumentsPath('procedures'))}
                >
                  {formatMessage(memberDetailMessages.procedures)}
                </Tab>
                <Tab
                  stopId="conditions"
                  onClick={() => history.push(getDocumentsPath('conditions'))}
                >
                  {formatMessage(memberDetailMessages.conditions)}
                </Tab>
                <Tab
                  stopId="allergies"
                  onClick={() => history.push(getDocumentsPath('allergies'))}
                >
                  {formatMessage(memberDetailMessages.allergies)}
                </Tab>
              </TabList>
            </div>
            <TabPanel stopId="documents" className={classes.tabPanel}>
              <SubjectViewerTableModule
                isModuleVisible={true}
                dateTickFilter={{ min: 0, max: 0 }}
                dateTickBounds={{ min: 0, max: 0 }}
                dateFilter={{ min: new Date(0), max: new Date() }}
                isInEditMode={false}
                deviceId="member-detail"
                onModuleConfigChange={() => undefined}
                onDynamicDataResolved={() => ({})}
                selectedDashboardConfig={documentsDashboardConfig}
                moduleKey={documentsModuleConfig.module.key}
                moduleConfig={documentsModuleConfig}
                patient={patient}
                dataSource={documentsModuleConfig.component.dataSource}
                columns={documentsModuleConfig.component.columns}
                momentFormat={documentsModuleConfig.component.momentFormat}
                pageSize={documentsModuleConfig.component.pageSize}
                useTagFilter={documentsModuleConfig.component.useTagFilter}
                useNotTagFilter={
                  documentsModuleConfig.component.useNotTagFilter
                }
                notTagFilters={documentsModuleConfig.component.notTagFilters}
                categories={documentsModuleConfig.component.categories}
                useCategoryFilter={
                  documentsModuleConfig.component.useCategoryFilter
                }
                timeFilterProps={
                  documentsModuleConfig.component.timeFilterProps
                }
                applyDateFilter={
                  documentsModuleConfig.component.applyDateFilter
                }
                filters={documentsModuleConfig.component.filters}
              />
            </TabPanel>
            <TabPanel stopId="notes" className={classes.tabPanel}>
              <NotesContent memberId={memberId} fallback={fallback} />
            </TabPanel>
            <TabPanel stopId="medications" className={classes.tabPanel}>
              <MedicationsContent memberId={memberId} fallback={fallback} />
            </TabPanel>
            <TabPanel stopId="procedures" className={classes.tabPanel}>
              <ProceduresContent memberId={memberId} fallback={fallback} />
            </TabPanel>
            <TabPanel stopId="conditions" className={classes.tabPanel}>
              <ConditionsContent memberId={memberId} fallback={fallback} />
            </TabPanel>
            <TabPanel stopId="allergies" className={classes.tabPanel}>
              <AllergiesContent memberId={memberId} fallback={fallback} />
            </TabPanel>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

const MemberDocuments: React.FC = () => {
  return <MemberDetail section="documents" />;
};

MemberDocuments.displayName = 'MemberDocuments';
export default MemberDocuments;
